import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  Fade,
  Slide,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, LockReset } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';
import axios from 'axios';

export default function ResetPassword(props) {
  const theme = useTheme();
  const history = useHistory();
  const token = props.match.params.token;
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const resetPasswordURL = `${process.env.REACT_APP_SERVER_API}/users/resetPassword`;
  const checkTokenURL = `${process.env.REACT_APP_SERVER_API}/users/checkToken`;

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({});

  const password = useRef({});
  password.current = watch('newPassword', '');

  useEffect(() => {
    axios
      .post(checkTokenURL, {
        token: token,
      })
      .then((res) => {
        setTokenValid(true);
      })
      .catch((err) => {
        setTokenValid(false);
      });
  }, [token, checkTokenURL]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      await axios.put(resetPasswordURL, {
        resetLink: token,
        newPassword: data.newPassword2,
      });
      alert('Your password has been reset, you can now login with your new password');
      history.push('/signin');
    } catch (err) {
      setIsLoading(false);
      if (err.response?.data) {
        setErrorMessage(err.response.data.message);
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    }
  };

  if (tokenValid === null) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!tokenValid) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
          position: 'fixed',
          top: 0,
          left: 0,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              p: 6,
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" color="error" sx={{ mb: 2, fontWeight: 700 }}>
              Invalid Link
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              This reset link is incorrect or has expired. Please request a new one.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              href="/forgetpassword"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Request New Link
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 50%, #334155 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.main} 100%)`,
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '100%',
          height: '100%',
          transform: 'translate(-50%, -50%)',
          background:
            theme.palette.mode === 'dark'
              ? `radial-gradient(circle, ${theme.alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`
              : `radial-gradient(circle, ${theme.alpha('#FFFFFF', 0.2)} 0%, transparent 70%)`,
          animation: 'pulse 15s ease-in-out infinite',
        },
        '@keyframes pulse': {
          '0%, 100%': {
            opacity: 1,
            transform: 'translate(-50%, -50%) scale(1)',
          },
          '50%': {
            opacity: 0.8,
            transform: 'translate(-50%, -50%) scale(1.1)',
          },
        },
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          transform: 'scale(0.8)',
          transformOrigin: 'center center',
        }}
      >
        <Slide direction="down" in={true} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              padding: { xs: 4, sm: 6 },
              background:
                theme.palette.mode === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(255, 255, 255, 0.3)'
              }`,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                  : '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 12px 48px 0 rgba(0, 0, 0, 0.5)'
                    : '0 12px 48px 0 rgba(31, 38, 135, 0.5)',
              },
            }}
          >
            {/* Logo and Title */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Fade in={true} timeout={1000}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    mb: 3,
                    boxShadow: `0 8px 24px ${theme.alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  <LockReset sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Fade>

              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Reset Password
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                Enter your new password below
              </Typography>
            </Box>

            {/* Error Alert */}
            {errorMessage && (
              <Fade in={true}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    animation: 'shake 0.5s',
                    '@keyframes shake': {
                      '0%, 100%': { transform: 'translateX(0)' },
                      '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-4px)' },
                      '20%, 40%, 60%, 80%': { transform: 'translateX(4px)' },
                    },
                  }}
                  onClose={() => setErrorMessage('')}
                >
                  {errorMessage}
                </Alert>
              </Fade>
            )}

            {/* Reset Password Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                autoFocus
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    '&.Mui-focused': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{
                          transition: 'transform 0.3s',
                          '&:hover': {
                            transform: 'rotate(180deg)',
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...register('newPassword', {
                  required: 'New password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                })}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPassword2 ? 'text' : 'password'}
                autoComplete="new-password"
                error={!!errors.newPassword2}
                helperText={errors.newPassword2?.message}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    '&.Mui-focused': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword2(!showPassword2)}
                        edge="end"
                        sx={{
                          transition: 'transform 0.3s',
                          '&:hover': {
                            transform: 'rotate(180deg)',
                          },
                        }}
                      >
                        {showPassword2 ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...register('newPassword2', {
                  required: 'Please confirm your password',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                  validate: (value) => value === password.current || 'Passwords do not match',
                })}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.8,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  mt: 1,
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: `0 4px 16px ${theme.alpha(theme.palette.primary.main, 0.4)}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${theme.alpha(theme.palette.primary.main, 0.5)}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  '&.Mui-disabled': {
                    background: theme.palette.action.disabledBackground,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={28} sx={{ color: 'white' }} />
                ) : (
                  'Reset Password'
                )}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <MuiLink
                  href="/forgetpassword"
                  variant="body2"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'all 0.3s',
                    '&:hover': {
                      textDecoration: 'underline',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  Resend reset link
                </MuiLink>
              </Box>
            </Box>
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
}
